import React, { createContext, useState, useContext, ReactNode, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from 'app/NewAIAvatarLayout/redux/headerSlice/selectors';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { sliceKey, reducer } from 'app/NewAIAvatarLayout/redux/headerSlice/slice';
import { headerSaga } from 'app/NewAIAvatarLayout/redux/headerSlice/saga';
import MiaChatV2ApiService from 'app/NewAIAvatarLayout/features/MiaChat/apiService/miaChatApiService';
import { ChatRequest, ChatResponse, Message, Operation, InitRequest, InitResponse, UpdateConversationStateRequest, UpdateConversationStateResponse, Segment, LatestPrompt } from 'app/NewAIAvatarLayout/features/MiaChat/types';

// Import mock toggle utility for console commands
import 'app/apiService/miaChat/mockToggle';


interface MiaChatContextType {
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  
  error: string;
  setError: React.Dispatch<React.SetStateAction<string>>;
  
  operations: Operation[];
  setOperations: React.Dispatch<React.SetStateAction<Operation[]>>;
  
  segments: Segment[];
  setSegments: React.Dispatch<React.SetStateAction<Segment[]>>;
  
  currentConversation: ChatResponse | null;
  setCurrentConversation: React.Dispatch<React.SetStateAction<ChatResponse | null>>;
  
  latestPrompts: LatestPrompt[];
  setLatestPrompts: React.Dispatch<React.SetStateAction<LatestPrompt[]>>;
  
  fetchOperations: () => Promise<void>;
  fetchLatestPrompts: (signal?: AbortSignal) => Promise<void>;
  initChat: (selected_operation?: string, signal?: AbortSignal) => Promise<InitResponse | null>;
  startNewChat: (action: string, conversationId: string, signal?: AbortSignal) => Promise<ChatResponse | null>;
  sendChatMessage: (request: ChatRequest, signal?: AbortSignal) => Promise<ChatResponse | null>;
  updateConversationState: (request: UpdateConversationStateRequest, signal?: AbortSignal) => Promise<UpdateConversationStateResponse | null>;
  getUniqueTags: () => string[];
  getOperationsByTag: (tag: string) => Operation[];
  getSegmentsAsOperations: () => Operation[];
}

// ----------- Context Create -----------
const MiaChatContext = createContext<MiaChatContextType | undefined>(undefined);

// ----------- Provider Component -----------
export const MiaChatProvider = ({ children }: { children: ReactNode }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [operations, setOperations] = useState<Operation[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [currentConversation, setCurrentConversation] = useState<ChatResponse | null>(null);
  const [latestPrompts, setLatestPrompts] = useState<LatestPrompt[]>([]);
  
  // Inject Header reducer and saga for dealer functionality
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: headerSaga });

  // Get selected dealer from header state
  const selectedDealer = useSelector(selectDealerSelected);
  const currentDealerId = selectedDealer?.dealer || '';

  // ----------- API Calls Using MiaChat Service -----------
  const fetchOperations = useCallback(async () => {
    if (!currentDealerId) {
      setError("No dealer selected");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await MiaChatV2ApiService.getOperations({
        dealer_id: currentDealerId
      });

      if (response.body?.operations) {
        setOperations(response.body.operations);
      }
      
      if (response.body?.segments) {
        setSegments(response.body.segments);
      }
      
      if (!response.body?.operations && !response.body?.segments) {
        setError("No operations or segments found");
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to fetch operations");
    } finally {
      setLoading(false);
    }
  }, [currentDealerId]);

  const initChat = useCallback(async (selected_operation?: string, signal?: AbortSignal): Promise<InitResponse | null> => {
    if (!currentDealerId) {
      setError("No dealer selected");
      return null;
    }

    setLoading(true);
    setError("");
    try {
      const payload: InitRequest = {
        dealer_id: currentDealerId
      };
      
      if (selected_operation) {
        payload.selected_operation = selected_operation;
      }
      
      const response = await MiaChatV2ApiService.initChat(payload, signal);

      // Check if request was aborted
      if (signal?.aborted) {
        return null;
      }

      if (response.body) {
        return response.body;
      } else {
        setError("Failed to initialize chat");
        return null;
      }
    } catch (err: unknown) {
      // Check if error is due to abort
      if (err instanceof Error && err.name === 'AbortError') {
        return null;
      }
      setError(err instanceof Error ? err.message : "Failed to initialize chat");
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentDealerId, setError, setLoading]);

  const startNewChat = useCallback(async (selected_option: string, conversationId: string, signal?: AbortSignal): Promise<ChatResponse | null> => {
    if (!currentDealerId) {
      setError("No dealer selected");
      return null;
    }

    if (!conversationId) {
      setError("Conversation ID is required");
      return null;
    }

    setLoading(true);
    setError("");
    try {
      // Fallback to API call for other operations
      const response = await MiaChatV2ApiService.startNewChat({
        dealer_id: currentDealerId,
        conversation_id: conversationId,
        action: "SELECT_OPERATION",
        selected_option,
        new: true,
        next_action: currentConversation?.next_action || '',
        messages: currentConversation?.messages || []
      }, signal);

      // Check if request was aborted
      if (signal?.aborted) {
        return null;
      }

      if (response.body) {
        setCurrentConversation(response.body);
        return response.body;
      } else {
        setError("Failed to start chat");
        return null;
      }
    } catch (err: unknown) {
      // Check if error is due to abort
      if (err instanceof Error && err.name === 'AbortError') {
        return null;
      }
      setError(err instanceof Error ? err.message : "Failed to start chat");
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentDealerId, currentConversation]);

  const sendChatMessage = useCallback(async (request: ChatRequest, signal?: AbortSignal): Promise<ChatResponse | null> => {
    setLoading(true);
    setError("");
    try {
      request.next_action = currentConversation?.next_action;
      // Fallback to API call for unmocked actions
      const response = await MiaChatV2ApiService.sendChatMessage(request, signal);

      // Check if request was aborted
      if (signal?.aborted) {
        return null;
      }

      if (response.body) {
        setCurrentConversation(response.body);
        return response.body;
      } else {
        setError("Failed to send message");
        return null;
      }
    } catch (err: unknown) {
      // Check if error is due to abort
      if (err instanceof Error && err.name === 'AbortError') {
        return null;
      }
      setError(err instanceof Error ? err.message : "Failed to send message");
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentConversation]);

  const updateConversationState = useCallback(async (request: UpdateConversationStateRequest, signal?: AbortSignal): Promise<UpdateConversationStateResponse | null> => {
    setLoading(true);
    setError("");
    try {
      const response = await MiaChatV2ApiService.updateConversationState(request, signal);

      // Check if request was aborted
      if (signal?.aborted) {
        return null;
      }

      if (response.body) {
        return response.body;
      } else {
        setError("Failed to update conversation state");
        return null;
      }
    } catch (err: unknown) {
      // Check if error is due to abort
      if (err instanceof Error && err.name === 'AbortError') {
        return null;
      }
      setError(err instanceof Error ? err.message : "Failed to update conversation state");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLatestPrompts = useCallback(async (signal?: AbortSignal): Promise<void> => {
    if (!currentDealerId) {
      setError("No dealer selected");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await MiaChatV2ApiService.getLatestPrompts(currentDealerId, signal);

      // Check if request was aborted
      if (signal?.aborted) {
        return;
      }

      if (response.body) {
        setLatestPrompts(response.body);
      } else {
        setError("Failed to fetch latest prompts");
      }
    } catch (err: unknown) {
      // Check if error is due to abort
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to fetch latest prompts");
    } finally {
      setLoading(false);
    }
  }, [currentDealerId]);

  // ----------- Utility Functions -----------
  
  // Get unique tags from all operations with "All" as default
  const getUniqueTags = useCallback((): string[] => {
    const allTags = operations.flatMap(op => op.tags);
    const uniqueTags = Array.from(new Set(allTags));
    return ['All', ...uniqueTags];
  }, [operations]);

  // Get operations filtered by specific tag
  const getOperationsByTag = useCallback((tag: string): Operation[] => {
    if (tag === 'All') {
      return operations;
    }
    return operations.filter(op => op.tags.includes(tag));
  }, [operations]);

  // Convert segments to operations format for display
  const getSegmentsAsOperations = useCallback((): Operation[] => {
    return segments.map((segment, index) => ({
      id: segment._id,
      action: "SELECT_OPERATION",
      display_text: segment.name,
      description: `Segment: ${segment.name}`,
      icon: "segment",
      tags: ["Segment", "Customer Group"]
    }));
  }, [segments]);

  return (
    <MiaChatContext.Provider
      value={{
        loading,
        setLoading,
        error,
        setError,
        operations,
        setOperations,
        segments,
        setSegments,
        currentConversation,
        setCurrentConversation,
        latestPrompts,
        setLatestPrompts,
        fetchOperations,
        fetchLatestPrompts,
        initChat,
        startNewChat,
        sendChatMessage,
        updateConversationState,
        getUniqueTags,
        getOperationsByTag,
        getSegmentsAsOperations,
      }}
    >
      {children}
    </MiaChatContext.Provider>
  );
};

// ----------- Custom Hook -----------
export const useMiaChat = (): MiaChatContextType => {
  const context = useContext(MiaChatContext);
  if (context === undefined) {
    throw new Error("useMiaChat must be used within a MiaChatProvider");
  }
  return context;
};
